import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProducts } from '../context/ProductsContext';
import Footer from '../components/Footer';

const HowItWorksPage: React.FC = () => {
  const navigate = useNavigate();
  const { products: apiProducts } = useProducts();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
  const [heroImage, setHeroImage] = useState<string>('');
  const [pressButtonIcon, setPressButtonIcon] = useState<string>('');
  const [connectIcon, setConnectIcon] = useState<string>('');
  const [helpFastIcon, setHelpFastIcon] = useState<string>('');
  const [productImages, setProductImages] = useState<{[key: string]: string}>({});
  const [bestForMeImage, setBestForMeImage] = useState<string>('');
  const [faqImage, setFaqImage] = useState<string>('');
  const [faqImage2, setFaqImage2] = useState<string>('');
  const [whereWorksImage, setWhereWorksImage] = useState<string>('');
  const [chooseHelperImage, setChooseHelperImage] = useState<string>('');
  const [mediaLogos, setMediaLogos] = useState<string[]>([]);

  useEffect(() => {
    // Fetch images
    fetch(`${API_URL}/api/images`)
      .then(res => res.json())
      .then((data: any) => {
        // Look for the hero image - woman fall (top section)
        const hero = data.images.find((img: any) => 
          img.filename.toLowerCase().replace(/[^a-z0-9]/g, '').includes('211howitworkswomanfall')
        );
        if (hero) {
          setHeroImage(`${API_URL}/api/images/${hero.id}`);
        }

        // Look for step icons - sales support microsite images
        const pressBtn = data.images.find((img: any) => 
          img.filename.toLowerCase().replace(/[^a-z0-9]/g, '').includes('salessupportmicrosite04') ||
          img.filename.toLowerCase().includes('microsite04')
        );
        if (pressBtn) setPressButtonIcon(`${API_URL}/api/images/${pressBtn.id}`);

        const connect = data.images.find((img: any) => 
          img.filename.toLowerCase().replace(/[^a-z0-9]/g, '').includes('salessupportmicrosite06') ||
          img.filename.toLowerCase().includes('microsite06')
        );
        if (connect) setConnectIcon(`${API_URL}/api/images/${connect.id}`);

        const helpFast = data.images.find((img: any) => 
          img.filename.toLowerCase().replace(/[^a-z0-9]/g, '').includes('salessupportmicrosite05') ||
          img.filename.toLowerCase().includes('microsite05')
        );
        if (helpFast) setHelpFastIcon(`${API_URL}/api/images/${helpFast.id}`);

        // Get product images from API products
        const imageMap: {[key: string]: string} = {};
        apiProducts.forEach(product => {
          if (product.imageFilename) {
            const img = data.images.find((i: any) => i.filename === product.imageFilename);
            if (img) {
              imageMap[product.shortName] = `${API_URL}/api/images/${img.id}`;
            }
          }
        });
        setProductImages(imageMap);

        // Best for me section image
        const bestForMe = data.images.find((img: any) => 
          img.filename === '211HowItWorksWomanRedShirt.png'
        );
        if (bestForMe) setBestForMeImage(`${API_URL}/api/images/${bestForMe.id}`);

        // FAQ section image 1 - EZ Care product image
        const faq1 = data.images.find((img: any) => 
          img.filename === 'ez-care-in-home-medical-alert-1.jpg'
        );
        if (faq1) setFaqImage(`${API_URL}/api/images/${faq1.id}`);

        // FAQ section image 2 - charging cradle
        const faq2 = data.images.find((img: any) => 
          img.filename === '1.-Alert1-Zoey-Product-Image-Update-On-the-Go-Medical-Alert-System-Charging-Cradle.jpg'
        );
        if (faq2) setFaqImage2(`${API_URL}/api/images/${faq2.id}`);

        // Where it works coverage map
        const whereWorks = data.images.find((img: any) => 
          img.filename === '211HowItWorksCoverageMap.png'
        );
        if (whereWorks) setWhereWorksImage(`${API_URL}/api/images/${whereWorks.id}`);

        // Choose helper section - woman with kids
        const chooseHelper = data.images.find((img: any) => 
          img.filename === '211HowItWorksWomanKids.jpg'
        );
        if (chooseHelper) setChooseHelperImage(`${API_URL}/api/images/${chooseHelper.id}`);

        // Look for media logos
        const logoFilenames = [
          'medicalalertsystemshomepageabc',
          'medicalalertsystemshomepagegma',
          'medicalalertsystemshomepagefronteirs',
          'medicalalertsystemshomepagefox',
          'medicalalertsystemshomepageflorida',
          'medicalalertsystemshomepagewbj',
          'medicalalertsystemshomepagela'
        ];
        
        const logos: string[] = [];
        logoFilenames.forEach(filename => {
          const logo = data.images.find((img: any) => 
            img.filename.toLowerCase().replace(/[^a-z0-9]/g, '').includes(filename.toLowerCase())
          );
          if (logo) {
            logos.push(`${API_URL}/api/images/${logo.id}`);
          }
        });
        setMediaLogos(logos);
      })
      .catch(err => console.error('Error fetching images:', err));
  }, [API_URL, apiProducts]);

  return (
    <div className="how-it-works-page">
      {/* Hero Section */}
      <section className="how-hero-section">
        <div className="how-hero-container">
          <div className="how-hero-content">
            <h1 className="how-hero-title">How Alert1 Medical Alert Systems Work</h1>
            
            <p className="how-hero-paragraph">
              Medical alert systems are easy to use. Whether you are wearing your emergency alert button as a necklace, on a belt clip, or as a wristband, just press the help button, tell the operator your emergency, and we'll send you help fast.
            </p>
            
            <p className="how-hero-paragraph">
              Even if you can't speak, we'll call your list of responders and stay on the line with you until help arrives.
            </p>
            
            <button 
              className="how-see-systems-btn"
              onClick={() => navigate('/medical-alerts')}
            >
              SEE MEDICAL ALERT SYSTEMS
            </button>
          </div>
          
          <div className="how-hero-image">
            {heroImage ? (
              <img src={heroImage} alt="Senior with medical alert" className="how-hero-img" />
            ) : (
              <div className="how-hero-placeholder">
                <svg width="400" height="400" viewBox="0 0 400 400" fill="none">
                  <circle cx="200" cy="200" r="180" fill="#f0f0f0"/>
                  <circle cx="200" cy="160" r="60" fill="#d0d0d0"/>
                  <ellipse cx="200" cy="300" rx="100" ry="120" fill="#d0d0d0"/>
                </svg>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Get Help Fast Section */}
      <section className="how-help-section">
        <div className="how-help-container">
          <h2 className="how-help-title">Get the help you need fast.</h2>
          
          <div className="how-help-steps">
            <div className="how-help-step">
              <div className="how-help-icon">
                {pressButtonIcon ? (
                  <img src={pressButtonIcon} alt="Press Button" className="how-help-icon-img" />
                ) : (
                  <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 11l3 3L22 4"/>
                    <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
                  </svg>
                )}
              </div>
              <h3 className="how-help-step-title">Press Button</h3>
              <p className="how-help-step-text">
                Your 100% showerproof help button will get you help for any type of emergency, big or small, 24/7.
              </p>
            </div>

            <div className="how-help-divider">
              <div className="how-help-divider-line"></div>
            </div>

            <div className="how-help-step">
              <div className="how-help-icon">
                {connectIcon ? (
                  <img src={connectIcon} alt="Connect" className="how-help-icon-img" />
                ) : (
                  <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 00-3-3.87"/>
                    <path d="M16 3.13a4 4 0 010 7.75"/>
                  </svg>
                )}
              </div>
              <h3 className="how-help-step-title">Connect</h3>
              <p className="how-help-step-text">
                A US-based operator from one of our Command Centers comes on the line to assist you in seconds. Even if you cannot speak, we will make sure you get help.
              </p>
            </div>

            <div className="how-help-divider">
              <div className="how-help-divider-line"></div>
            </div>

            <div className="how-help-step">
              <div className="how-help-icon">
                {helpFastIcon ? (
                  <img src={helpFastIcon} alt="Get Help Fast" className="how-help-icon-img" />
                ) : (
                  <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="1" y="3" width="15" height="13"/>
                    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
                    <circle cx="5.5" cy="18.5" r="2.5"/>
                    <circle cx="18.5" cy="18.5" r="2.5"/>
                  </svg>
                )}
              </div>
              <h3 className="how-help-step-title">Get Help Fast</h3>
              <p className="how-help-step-text">
                We stay on the line with you until help arrives so that you are never alone. We reduce worry by notifying your personal emergency contacts if EMT services are sent.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="how-products-section">
        <div className="how-products-container">
          <h2 className="how-products-title">Peace of mind at home and on the go</h2>

          <div className="how-products-grid">
            {apiProducts
              .filter(p => p.isActive && !p.shortName.includes('-fall'))
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map(product => {
                const productPath = `/products/${product.shortName}`;
                // Get annual plan price
                const annualPlan = product.pricingPlans?.find((p: any) => p.billingCycle === 'annual');
                const price = annualPlan?.monthlyPrice || product.basePrice || 0;
                
                // Get product-specific features
                const getFeatures = () => {
                  if (product.shortName === 'ez-care') return ['Landline', 'Fall Detection Available'];
                  if (product.shortName === 'ez-care-plus') return ['Wireless', 'Fall Detection Available'];
                  if (product.shortName === 'lifebeacon') return ['Phone Not Required', 'Fall Detection Available', 'Built-In Speaker'];
                  return [];
                };
                
                return (
                  <div key={product.id} className="how-product-card">
                    <Link
                      to={productPath}
                      className="how-product-image-link"
                      aria-label={`View ${product.name} details`}
                    >
                      <div className="how-product-image-wrapper">
                        {productImages[product.shortName] ? (
                          <img src={productImages[product.shortName]} alt={product.name} className="how-product-image" />
                        ) : (
                          <div className="how-product-placeholder">
                            <svg width="200" height="150" viewBox="0 0 200 150" fill="none">
                              <rect width="200" height="150" fill="#f0f0f0"/>
                            </svg>
                          </div>
                        )}
                      </div>
                    </Link>
                    <h3 className="how-product-name">
                      <Link to={productPath} className="how-product-name-link">
                        {product.name}
                      </Link>
                    </h3>
                    <p className="how-product-price">FROM ${price.toFixed(2)}</p>
                    <ul className="how-product-features">
                      {getFeatures().map((feat, idx) => (
                        <li key={idx}>{feat}</li>
                      ))}
                    </ul>
                    <Link to={productPath} className="how-product-button">
                      Learn More
                    </Link>
                  </div>
                );
              })}
          </div>
        </div>
      </section>

      {/* Best For Me Section */}
      <section className="how-best-section">
        <div className="how-best-container">
          <div className="how-best-content">
            <h2 className="how-best-title">Which Medical Alert System is Best for Me?</h2>
            
            <div className="how-best-question">
              <h3 className="how-best-question-title">What style of wearable help button do you prefer?</h3>
              <p className="how-best-question-text">
                Choose from necklace, wristband, or belt clip styles to protect you at home or on the go. Fall detection can be added to any style help button.
              </p>
            </div>

            <div className="how-best-question">
              <h3 className="how-best-question-title">Would you like fall protection at home only?</h3>
              <p className="how-best-question-text">
                Choose our home medical alert with fall detection sensors. Wear your emergency button alarm as a pendant (outside your clothes at about heart level), and when you fall, the help button can detect your fall and send out an alert for help automatically. You will get help even if you can't press your button. If you prefer a bracelet style, our mobile wristband with GPS has optional fall detection and can be used inside the home as well as outdoors.
              </p>
            </div>

            <div className="how-best-question">
              <h3 className="how-best-question-title">Would you like mobile coverage?</h3>
              <p className="how-best-question-text">
                Alert1 offers many affordable mobile medical alert devices and styles for 24/7 protection and easy access to help when you're on the go.
              </p>
            </div>

            <div className="how-best-question">
              <h3 className="how-best-question-title">Would you like fall detection added to your mobile alert with GPS?</h3>
              <p className="how-best-question-text">
                Choose either our mobile fall detection medical alert pendant or wristband. Either of these fall alert devices can sense falls and automatically contact the trained agents at our US-based Command Center. If a fall is detected, your medical alert device will summon help without your needing to press the alert button.
              </p>
            </div>

            <div className="how-best-question">
              <h3 className="how-best-question-title">Would you like maximum protection both at home and on the go?</h3>
              <p className="how-best-question-text">
                Choose our mobile + home fall detection package for all of your daily needs.
              </p>
              <button 
                className="how-pricing-btn"
                onClick={() => navigate('/pricing')}
              >
                SEE ALERT1 PRICING PLANS
              </button>
            </div>
          </div>
          
          <div className="how-best-image">
            {bestForMeImage ? (
              <img src={bestForMeImage} alt="Senior eating corn" className="how-best-img" />
            ) : (
              <div className="how-best-placeholder">
                <svg width="400" height="400" viewBox="0 0 400 400" fill="none">
                  <circle cx="200" cy="200" r="180" fill="#f0f0f0"/>
                  <circle cx="200" cy="160" r="60" fill="#d0d0d0"/>
                  <ellipse cx="200" cy="300" rx="100" ry="120" fill="#d0d0d0"/>
                </svg>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="how-faq-section">
        <div className="how-faq-container-new">
          <div className="how-faq-left-column">
            <div className="how-faq-image-wrapper">
              {faqImage ? (
                <img src={faqImage} alt="Medical Alert Device" className="how-faq-img" />
              ) : (
                <div className="how-faq-placeholder">
                  <svg width="400" height="400" viewBox="0 0 400 400" fill="none">
                    <circle cx="200" cy="200" r="180" fill="#f0f0f0"/>
                  </svg>
                </div>
              )}
            </div>

            <div className="how-faq-item">
              <h3 className="how-faq-question">Q. Who helps me after I press the help button?</h3>
              <p className="how-faq-answer"><strong>A.</strong> When you press the emergency help button, a specialized agent at our 24/7 Command Center gets on the line. After checking your status, our agent will call your customized personal contacts list and/or emergency services-- whatever you need and want. Our agent will stay on the line until help arrives.</p>
            </div>

            <div className="how-faq-item">
              <h3 className="how-faq-question">Q. Can I wear my help button in the shower?</h3>
              <p className="how-faq-answer"><strong>A.</strong> Yes, all Alert1 medical alert pendants and wristbands are water resistant. We recommend you wear your alert device when showering, as the risk for falls increases in the shower.</p>
            </div>

            <div className="how-faq-item">
              <h3 className="how-faq-question">Q. How can I wear my Alert1 life-saving medical alert?</h3>
              <p className="how-faq-answer"><strong>A.</strong> Your medical alert button can be worn in one of the following ways:</p>
              <ol className="how-faq-list">
                <li>Necklace/Pendant</li>
                <li>Wristband/Bracelet</li>
                <li>Belt Clip</li>
              </ol>
            </div>
          </div>

          <div className="how-faq-right-column">
            <h2 className="how-faq-title">Medical Alert FAQ</h2>

            <div className="how-faq-item">
              <h3 className="how-faq-question">Q. Can AlertOne medical alerts detect falls? How does fall detection work?</h3>
              <p className="how-faq-answer"><strong>A.</strong> Yes, Alert One offers medical alerts with advanced fall detection technology. Even if you fall and can't speak, our fall alert devices can detect your fall with 98% accuracy and signal our 24/7 Command Center for help.</p>
              <p className="how-faq-answer">Fall Detection alerts have built-in sensors that measure speed, impact, and lack of movement. When a fall is detected, the sensors automatically send a help signal to our Command Center. Because fall detection does not detect 100% of falls, you should always press your button if you are able.</p>
            </div>

            <div className="how-faq-item">
              <h3 className="how-faq-question">Q. Do Alert 1 medical alerts work in my area? Do I need internet or phone service?</h3>
              <p className="how-faq-answer"><strong>A.</strong> Alert 1 serves members in all 50 states, as well as Canada and Puerto Rico. Depending on the device, Alert1 medical alerts use the AT&T network to connect with our Command Center. Our med alert devices are self-contained, complete systems that require nothing additional to function. We ship them to you completely ready to use; you do not have to get internet or phone service.</p>
            </div>

            <div className="how-faq-image-wrapper">
              {faqImage2 ? (
                <img src={faqImage2} alt="Medical Alert Charging Cradle" className="how-faq-img" />
              ) : (
                <div className="how-faq-placeholder">
                  <svg width="400" height="400" viewBox="0 0 400 400" fill="none">
                    <circle cx="200" cy="200" r="180" fill="#f0f0f0"/>
                  </svg>
                </div>
              )}
            </div>

            <button 
              className="how-protection-btn"
              onClick={() => navigate('/pricing')}
            >
              GET PROTECTION TODAY
            </button>
          </div>
        </div>
      </section>

      {/* Where It Works Section */}
      <section className="how-where-works-section">
        <div className="how-where-works-container">
          <div className="how-where-works-content">
            <h2 className="how-where-works-title">Where It Works</h2>
            
            <p className="how-where-works-paragraph">
              Alert1 covers all 50 US states, Canada, and Puerto Rico. Alert 1 uses AT&T service to provide the strongest connection when you need help. Whether you're at home or traveling across the country, you can trust Alert1 to protect you 24/7.
            </p>
            
            <p className="how-where-works-paragraph">
              If you are using a home medical alert, the help button works up to a 400 feet range from the base unit. This range can vary. We encourage you to test your medical alert throughout your home.
            </p>
            
            <p className="how-where-works-paragraph">
              You can find more information on Alert1's coverage area here:
            </p>
            
            <button 
              className="how-coverage-btn"
              onClick={() => navigate('/where-it-works')}
            >
              MORE COVERAGE INFO
            </button>
          </div>
          
          <div className="how-where-works-image">
            {whereWorksImage ? (
              <img src={whereWorksImage} alt="Global Coverage Map" className="how-where-works-img" />
            ) : (
              <div className="how-where-works-placeholder">
                <svg width="440" height="440" viewBox="0 0 440 440" fill="none">
                  <circle cx="220" cy="220" r="200" fill="#1e3a8a"/>
                  <circle cx="220" cy="220" r="180" fill="#2563eb"/>
                  <circle cx="180" cy="200" r="10" fill="#dc2626"/>
                  <circle cx="200" cy="180" r="10" fill="#dc2626"/>
                  <circle cx="220" cy="190" r="10" fill="#dc2626"/>
                  <circle cx="240" cy="200" r="10" fill="#dc2626"/>
                  <circle cx="260" cy="180" r="10" fill="#dc2626"/>
                </svg>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Choose Helper Section */}
      <section className="how-choose-helper-section">
        <div className="how-choose-helper-container">
          <div className="how-choose-helper-image-wrapper">
            {chooseHelperImage ? (
              <img src={chooseHelperImage} alt="Grandmother with grandchildren" className="how-choose-helper-img" />
            ) : (
              <div className="how-choose-helper-placeholder">
                <svg width="400" height="400" viewBox="0 0 400 400" fill="none">
                  <circle cx="200" cy="200" r="180" fill="#f0f0f0"/>
                  <circle cx="150" cy="150" r="40" fill="#d0d0d0"/>
                  <circle cx="250" cy="150" r="40" fill="#d0d0d0"/>
                  <circle cx="200" cy="250" r="50" fill="#d0d0d0"/>
                </svg>
              </div>
            )}
          </div>

          <div className="how-choose-helper-content">
            <h2 className="how-choose-helper-title">Can I Choose Who Helps Me?</h2>
            <p className="how-choose-helper-text">
              Alert1 lets you choose who comes to help you. Upon joining Alert1, you get to create a personalized list of emergency contacts and the order in which you would like us to contact them.
            </p>
            <p className="how-choose-helper-text">
              When you press your help button, you will be connected to our Command Center. Our trained operators will call the contacts you have already selected, in the order in which you've indicated, and then stay on the line with you until help arrives.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="how-testimonial-section">
        <div className="how-testimonial-container">
          <div className="how-testimonial-stars">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="#f4c430">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="#f4c430">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="#f4c430">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="#f4c430">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="#f4c430">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
          </div>
          <p className="how-testimonial-quote">
            "When I push my button, <strong>Alert1 answers immediately</strong> and asks if I need help. Then they call my daughter, neighbor or 911 if I need. It couldn't be faster or easier!"
          </p>
          <p className="how-testimonial-author"><strong><em>Shirley — Ohio</em></strong></p>
          <Link to="/reviews" className="how-reviews-btn">SEE MORE REVIEWS</Link>
        </div>
      </section>

      {/* Trusted and Recommended Section */}
      <section className="how-trusted-section">
        <div className="how-trusted-container">
          <h2 className="how-trusted-title">Trusted and Recommended</h2>
          <div className="how-trusted-logos">
            <div className="how-trusted-logo-row">
              {mediaLogos.slice(0, 4).map((logo, index) => (
                <div key={index} className="how-trusted-logo">
                  <img src={logo} alt="Media Logo" />
                </div>
              ))}
            </div>
            <div className="how-trusted-logo-row">
              {mediaLogos.slice(4, 7).map((logo, index) => (
                <div key={index} className="how-trusted-logo">
                  <img src={logo} alt="Media Logo" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default HowItWorksPage;
